/**
 * ICCAGRChart — Invested Capital CAGR over the available data window.
 *
 * Shows the IC CAGR as a prominent stat + sparkline of IC values.
 */
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, CardHeader, CardBody } from '../../ui/Card';
import { PEER_COLORS, gridProps, xAxisProps, CHART_INK, CHART_TYPE } from './chartTheme';
import { formatFiscalYear, formatCurrencyValue } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

function computeCAGR(first: number, last: number, years: number): number | null {
  if (first <= 0 || last <= 0 || years <= 0) return null;
  return (Math.pow(last / first, 1 / years) - 1) * 100;
}

export default function ICCAGRChart({ data, region }: Props) {
  const valid = data.filter((d) => d.invested_capital != null && d.invested_capital > 0);
  if (valid.length < 2) return null;

  const first = valid[0].invested_capital!;
  const last = valid[valid.length - 1].invested_capital!;
  const cagr = computeCAGR(first, last, valid.length - 1);

  const chartData = valid.map((d) => ({
    year: formatFiscalYear(d.year, region),
    ic: d.invested_capital,
  }));

  const isPositive = cagr != null && cagr >= 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-neutral-800">Invested Capital CAGR</h3>
            <p className="text-xs text-neutral-500 mt-0.5">
              {valid.length}-year IC growth — {formatCurrencyValue(first, region, true)} → {formatCurrencyValue(last, region, true)}
            </p>
          </div>
          {cagr != null && (
            <span className={`text-xl font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
              {isPositive ? '+' : ''}{cagr.toFixed(1)}%
            </span>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="icGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={PEER_COLORS[0]} stopOpacity={0.25} />
                <stop offset="95%" stopColor={PEER_COLORS[0]} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="year" {...xAxisProps} />
            <YAxis width={80} tick={{ fontSize: CHART_TYPE.axisTick, fill: CHART_INK.label }} tickFormatter={(v) => formatCurrencyValue(v, region, true)} />
            <Tooltip formatter={(v: number) => [formatCurrencyValue(v, region), 'Invested Capital']} />
            <Area
              type="monotone"
              dataKey="ic"
              name="Invested Capital"
              stroke={PEER_COLORS[0]}
              fill="url(#icGradient)"
              strokeWidth={2}
              dot={{ r: 3, fill: PEER_COLORS[0] }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardBody>
    </Card>
  );
}
